export const DEFAULT_CURSOR_IMAGE_DURATION_SEC = 5;
export const MIN_CURSOR_IMAGE_DURATION_SEC = 1e-3;

export interface ImageAtScriptCursorWindow {
    startSec: number;
    endSec: number;
    durationSec: number;
}

/**
 * Resolve a ScriptClip source boundary to absolute composition time.
 *
 * Unlike getScenes().timelineStartSec, this accounts for collapsed cuts and
 * per-scene playback rate. At an inserted-time hole, the same source boundary
 * has a left and right composition edge; a scene starts at the left edge.
 */
export const resolveScriptBoundaryCompositionTime = (args: {
    scriptClip: any;
    scriptLocalSec: number;
}): number | null => {
    const scriptClip = args.scriptClip;
    const scriptLocalSec = Number(args.scriptLocalSec);
    if (!scriptClip || !Number.isFinite(scriptLocalSec)) return null;

    const EPS = 1e-3;
    let segments: any[] = [];
    try {
        segments = Array.isArray(scriptClip.scriptSegments)
            ? [...scriptClip.scriptSegments]
            : [];
    } catch {}
    segments.sort(
        (first, second) =>
            Number(first?.compStartSec ?? 0) - Number(second?.compStartSec ?? 0),
    );

    let previousEdge: number | null = null;
    let nextEdge: number | null = null;
    let containingTime: number | null = null;
    let hasPreviousBoundaryAtRequestedTime = false;

    for (const segment of segments) {
        const sourceStartSec = Number(segment?.srcStartSec ?? NaN);
        const sourceEndSec = Number(segment?.srcEndSec ?? NaN);
        const compositionStartSec = Number(segment?.compStartSec ?? NaN);
        const playbackRateRaw = Number(segment?.playbackRate ?? 1);
        const playbackRate =
            Number.isFinite(playbackRateRaw) && playbackRateRaw > 0
                ? playbackRateRaw
                : 1;
        if (
            !Number.isFinite(sourceStartSec) ||
            !Number.isFinite(sourceEndSec) ||
            !Number.isFinite(compositionStartSec) ||
            sourceEndSec < sourceStartSec
        ) {
            continue;
        }

        const compositionEndSec =
            compositionStartSec + (sourceEndSec - sourceStartSec) / playbackRate;
        if (sourceEndSec <= scriptLocalSec + EPS) {
            previousEdge =
                previousEdge == null
                    ? compositionEndSec
                    : Math.max(previousEdge, compositionEndSec);
        }
        if (Math.abs(sourceEndSec - scriptLocalSec) <= EPS) {
            hasPreviousBoundaryAtRequestedTime = true;
        }
        if (sourceStartSec >= scriptLocalSec - EPS) {
            nextEdge =
                nextEdge == null
                    ? compositionStartSec
                    : Math.min(nextEdge, compositionStartSec);
        }
        if (
            scriptLocalSec >= sourceStartSec - EPS &&
            scriptLocalSec < sourceEndSec - EPS
        ) {
            containingTime =
                compositionStartSec +
                Math.max(0, scriptLocalSec - sourceStartSec) / playbackRate;
        }
    }

    // A discontinuity at one source boundary is inserted composition time.
    // The empty scene starts before that hole, not at the following segment.
    if (
        previousEdge != null &&
        nextEdge != null &&
        nextEdge > previousEdge + EPS &&
        hasPreviousBoundaryAtRequestedTime
    ) {
        return previousEdge;
    }
    if (containingTime != null) return containingTime;
    if (nextEdge != null) return nextEdge;
    if (previousEdge != null) return previousEdge;

    const clipStartSec = Number(scriptClip?.start?.seconds ?? 0);
    const clipInSec = Number(scriptClip?.range?.[0]?.seconds ?? 0);
    const rateRaw = Number(scriptClip?.playbackRate ?? 1);
    const rate = Number.isFinite(rateRaw) && rateRaw > 0 ? rateRaw : 1;
    return Number.isFinite(clipStartSec) && Number.isFinite(clipInSec)
        ? clipStartSec + Math.max(0, scriptLocalSec - clipInSec) / rate
        : null;
};

/**
 * Resolve the ordinary timeline window for an image chosen from the transcript
 * cursor flow. Images do not alter the ScriptClip; their default five-second
 * duration is simply clamped to the existing ScriptClip end.
 */
export const resolveImageAtScriptCursorWindow = (args: {
    timelineStartSec: number;
    scriptEndSec: number;
    defaultDurationSec?: number;
}): ImageAtScriptCursorWindow | null => {
    const startSec = Number(args.timelineStartSec);
    const scriptEndSec = Number(args.scriptEndSec);
    const requestedDurationSec = Number(
        args.defaultDurationSec ?? DEFAULT_CURSOR_IMAGE_DURATION_SEC,
    );

    if (
        !Number.isFinite(startSec) ||
        !Number.isFinite(scriptEndSec) ||
        !Number.isFinite(requestedDurationSec) ||
        requestedDurationSec <= 0
    ) {
        return null;
    }

    const availableDurationSec = scriptEndSec - startSec;
    if (availableDurationSec <= MIN_CURSOR_IMAGE_DURATION_SEC) return null;

    const durationSec = Math.min(requestedDurationSec, availableDurationSec);
    return {
        startSec,
        endSec: startSec + durationSec,
        durationSec,
    };
};
